import { useEffect, useState } from "react";
import {
  loadRemoteDefinitionModule,
  readCachedRemoteModule,
  type RemoteDefinition,
} from "../lib/remotes";
import type { RemoteAppComponent, RemoteAppModule } from "../types";

type RemoteState = {
  status: "idle" | "loading" | "ready" | "error";
  requestKey?: string;
  error?: string;
  component?: RemoteAppComponent;
};

function isRemoteAppModule(module: RemoteAppModule): module is { default: RemoteAppComponent } {
  return typeof module === "object" && module !== null && "default" in module;
}

async function loadRemote(
  definition: RemoteDefinition,
  forceRefresh: boolean,
) {
  const module = await loadRemoteDefinitionModule(definition, { forceRefresh });
  return isRemoteAppModule(module) ? module.default : module;
}

export function useRemoteModule(
  definition?: RemoteDefinition,
  retryNonce = 0,
) {
  const [state, setState] = useState<RemoteState>({ status: "idle" });

  useEffect(() => {
    if (!definition?.loader) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    const requestKey = `${definition.key}:${retryNonce}`;

    if (retryNonce === 0) {
      const cachedModule = readCachedRemoteModule(definition.key);
      if (cachedModule) {
        setState({
          status: "ready",
          requestKey,
          component: isRemoteAppModule(cachedModule)
            ? cachedModule.default
            : cachedModule,
        });
        return;
      }
    }

    setState({ status: "loading", requestKey });

    loadRemote(definition, retryNonce > 0)
      .then((component) => {
        if (!cancelled) {
          setState({ status: "ready", requestKey, component });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            status: "error",
            requestKey,
            error:
              error instanceof Error
                ? error.message
                : "No fue posible cargar la app.",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [definition, retryNonce]);

  if (definition?.key && state.requestKey !== `${definition.key}:${retryNonce}`) {
    return {
      status: "loading" as const,
      error: undefined,
      component: undefined,
    };
  }

  return {
    status: state.status,
    error: state.error,
    component: state.component,
  };
}
